import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import styled from "styled-components";
import { TableCellContext, TableColumn, resolveTablePreset } from "./tablePresets";
import { WidgetEntity } from "../types";
import { msgs } from "../msgs";
import { usePanel } from "../PanelContext";
import { exportTableToXlsx } from "../xlsxTable";
import { DARK_BTN, DARK_BTN_HOVER } from "../constants";
import { ReactComponent as ExpandIcon } from "../assets/expand.svg";
import { ReactComponent as DownloadIcon } from "../assets/download.svg";

// Small charcoal pill shared by the two table actions (expand / export). Background
// swaps between `$bg`/`$bgHover` on hover (tracked in JS since Tailwind's `hover:`
// can't reach an arbitrary per-instance color passed as a prop).
const DarkButtonRoot = styled.button<{
  $hover: boolean;
  $bg: string;
  $bgHover: string;
  $width?: number;
}>`
  background: ${p => (p.$hover ? p.$bgHover : p.$bg)};
  width: ${p => (p.$width !== undefined ? `${p.$width}px` : "auto")};
`;

const TableScroll = styled.div`
  max-height: 552px;
  box-shadow: 0 4px 8px 0 #00000014;
`;

export interface WidgetTableProps {
  content: unknown;
  propertyTicker?: string;
  /** Domain entity of the rows, used to pick the preset deterministically. */
  entity?: WidgetEntity;
}

const formatValue = (val: unknown): string =>
  typeof val === "boolean" ? (val ? "✓" : "✗") : String(val ?? "");

// The rendered cell strips the "GROUP_" prefix and localizes the rate type; mirror
// that here so the exported spreadsheet reads like what the user sees on screen.
const exportValue = (col: TableColumn, raw: unknown, intl: ReturnType<typeof useIntl>): string => {
  const display = formatValue(raw);
  if (col.key === "group") return display.replace(/^GROUP_/, "");
  if (col.key === "rateType") {
    if (display === "PERCENTAGE") return intl.formatMessage(msgs.rateTypePercentage);
    if (display === "CONSTANT") return intl.formatMessage(msgs.rateTypeConstant);
  }
  return display;
};

function DarkButton({
  onClick,
  icon,
  label,
  width,
  bg = DARK_BTN,
  bgHover = DARK_BTN_HOVER,
}: {
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  width?: number;
  bg?: string;
  bgHover?: string;
}): React.JSX.Element {
  const [hover, setHover] = useState(false);
  return (
    <DarkButtonRoot
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      $hover={hover}
      $bg={bg}
      $bgHover={bgHover}
      $width={width}
      className="inline-flex items-center justify-center gap-1.5 rounded-md px-2 py-1 h-[26px] text-xs font-medium text-white transition-colors focus:outline-none whitespace-nowrap">
      {icon}
      <span>{label}</span>
    </DarkButtonRoot>
  );
}

/**
 * Generic renderer for any array of flat objects. By default it shows one column per key
 * (raw header, stringified value). Domain-specific tables (e.g. promotions) opt into
 * custom columns, ordering, localized headers and cell renderers through a TablePreset —
 * see tablePresets.tsx. The widget stays free of any domain knowledge.
 *
 * When the table is wider than the panel it shows an "expand" action that widens the
 * whole sidebar (via PanelContext) so the full width becomes visible, and it can always
 * be exported to .xlsx.
 */
export function WidgetTable({
  content,
  propertyTicker,
  entity,
}: WidgetTableProps): React.JSX.Element | null {
  const intl = useIntl();
  const { isWidthExpanded, requestExpand } = usePanel();

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [overflowing, setOverflowing] = useState(false);

  const rows = Array.isArray(content) ? (content as Record<string, unknown>[]) : [];

  // Re-measure whenever the layout could change (panel width toggles, viewport
  // resizes, content changes). scrollWidth > clientWidth means columns are clipped.
  const measure = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setOverflowing(el.scrollWidth - el.clientWidth > 1);
  }, []);

  useLayoutEffect(() => {
    measure();
  }, [measure, isWidthExpanded, rows.length]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);

  if (rows.length === 0) return null;

  const allKeys = Object.keys(rows[0]);
  const preset = resolveTablePreset(allKeys, entity);
  // A preset defines explicit, ordered columns; otherwise derive a plain column per key.
  const baseColumns: TableColumn[] = preset ? preset.columns : allKeys.map(key => ({ key }));

  // When listing from an aggregator, each row can carry its own child engine's ticker
  // (row.propertyTicker) rather than — or in addition to — the current engine's own.
  // It's not always present (e.g. a single-engine listing), so show it as a leading
  // column only when the data actually has it, and only if a preset hasn't already
  // claimed that key for something else.
  const hasChildTicker =
    allKeys.includes("propertyTicker") && !baseColumns.some(col => col.key === "propertyTicker");
  const columns: TableColumn[] = hasChildTicker
    ? [{ key: "propertyTicker", labelMsg: msgs.fieldPropertyTicker }, ...baseColumns]
    : baseColumns;

  const headerLabel = (col: TableColumn): string =>
    col.labelMsg ? intl.formatMessage(col.labelMsg) : col.key;

  const handleExport = () => {
    const headers = columns.map(headerLabel);
    const data = rows.map(row => columns.map(col => exportValue(col, row[col.key], intl)));
    const name = `${entity ?? "table"}.xlsx`;
    void exportTableToXlsx(headers, data, name);
  };

  // Only offer the expand action when it would actually help: the table is clipped
  // and the panel isn't already wide. Deliberately NOT tied to the panel's general
  // "isExpanded" — the header button can make the panel taller (vertical expand)
  // without touching its width, and the table should still offer to widen it then.
  const canExpand = overflowing && !isWidthExpanded;

  // Grow the panel to the table's full natural width so no horizontal scroll is
  // needed (capped to the viewport by the panel itself).
  const handleExpand = () => requestExpand(scrollRef.current?.scrollWidth);

  return (
    <div className="my-1 space-y-[13px]">
      {canExpand && (
        <DarkButton
          onClick={handleExpand}
          icon={<ExpandIcon className="w-3.5 h-3.5" />}
          label={intl.formatMessage(msgs.tableExpandFull)}
          width={248}
          bg="#656565"
          bgHover="#555555"
        />
      )}

      <TableScroll ref={scrollRef} className="overflow-auto rounded-lg bg-white">
        <table className="w-full text-xs border-collapse bg-white">
          <thead>
            <tr>
              {columns.map(col => (
                <th
                  key={col.key}
                  className="px-3 py-2 text-left font-semibold uppercase text-[#6A7896] bg-[#F5F5F5] border-b border-[#F5F5F5] whitespace-nowrap">
                  {headerLabel(col)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const ctx: TableCellContext = { row, propertyTicker };
              return (
                <tr key={i} className="bg-white">
                  {columns.map(col => {
                    const display = formatValue(row[col.key]);
                    return (
                      <td
                        key={col.key}
                        className="px-3 py-2 text-[#212121] border-b border-[#F5F5F5] whitespace-nowrap">
                        {col.renderCell ? col.renderCell(display, ctx) : display}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableScroll>

      <div className="flex justify-end">
        <DarkButton
          onClick={handleExport}
          icon={<DownloadIcon className="w-3.5 h-3.5" />}
          label={intl.formatMessage(msgs.exportXlsx)}
          bg="#656565"
          bgHover="#555555"
        />
      </div>
    </div>
  );
}
